import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
    CreateCellClickHandler,
    createCellCssClassRules,
    CustomCellClickAction,
    GridCellConfig,
    RemoveRowHandler,
    useCellChangeHandler,
    useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import {
    defaultColDef,
    getDefaultRowIndexColumnConfig,
    getRowStyleFlags,
} from "src/utils/grid/gridStyleUtils";

import { SYS1020_R01Dto } from "./Dto_SYS1020_R01";
import { useSYS1020_R01Store } from "./Store_SYS1020_R01";

const COLUMN_STATE_KEY = "SYS1020_R01-grid-column-state";
const editableColumns = ["messageSrc", "messageType", "messageCd", "messageNm"];
const insertedOnlyColumns = ["messageSrc", "messageType", "messageCd"];

const headerNames: Record<string, string> = {
    rowIndex: "",
    messageSrc: "발생구분",
    messageType: "메시지구분",
    messageCd: "메시지코드",
    messageNm: "메시지명",
};

interface Props {
    clickable: boolean;
    setClickable: (clickable: boolean) => void;
    gridRef: React.RefObject<AgGridReact<SYS1020_R01Dto> | null>;
    defaultSYS1020_R01Row: SYS1020_R01Dto;
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number) => void;
}

export const Table_SYS1020_R01 = ({
    clickable,
    setClickable,
    gridRef,
    defaultSYS1020_R01Row,
    handleInsertClick,
}: Props) => {
    const {
        SYS1020_R01List,
        removeSYS1020_R01List,
        updateSYS1020_R01List,
        selectedSYS1020_R01List,
        addSelectedSYS1020_R01List,
        removeSelectedSYS1020_R01List,
        initSelectedSYS1020_R01List,
        allSelectedSYS1020_R01List,
        deletedSYS1020_R01List,
        addDeletedSYS1020_R01List,
        originalSYS1020_R01List,
        columnState,
        setColumnState,
        scrollTop,
        setScrollTop,
    } = useSYS1020_R01Store();
    const { messageSrcList, messageTypeList } = useCommonCodeStore();
    const {
        getRowId,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
        renderIndexCell,
    } = useGridSettingContent({
        gridRef,
        key: "valueIndex",
        gridData: SYS1020_R01List,
        defaultGridData: defaultSYS1020_R01Row,
        selectedRow: selectedSYS1020_R01List,
        originalGridData: originalSYS1020_R01List,
        updateRow: updateSYS1020_R01List,
    });

    const cellConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [],
        insertedDisabledCells: [],
        commonDisabledCells: ["messageSrc", "messageType", "messageCd"],
        pointerCells: ["messageNm"],
        insertedPointerCells: ["messageSrc", "messageType", "messageCd"],
        commonPointerCells: [],
        alignLeftCells: ["messageSrc", "messageType", "messageCd", "messageNm"],
    }), []);

    const {
        disabledInsertedColumns,
        disabledColumns,
        pointerInsertedColumns,
        pointerColumns,
        alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cellConfig), [cellConfig]);

    const gridDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<SYS1020_R01Dto>({
            idKey: "valueIndex",
            selectedList: selectedSYS1020_R01List,
            deletedList: deletedSYS1020_R01List,
            disabledInsertedColumns,
            disabledColumns,
            pointerInsertedColumns,
            pointerColumns,
            alignLeftColumns,
        }),
    }), [
        alignLeftColumns,
        createCellClassRules,
        deletedSYS1020_R01List,
        disabledColumns,
        disabledInsertedColumns,
        pointerColumns,
        pointerInsertedColumns,
        selectedSYS1020_R01List,
    ]);

    const baseColumnDefs = useMemo<ColDef<SYS1020_R01Dto>[]>(() => [
        ...getDefaultRowIndexColumnConfig((params) => renderIndexCell(
            params,
            (row) => getRowStyleFlags(row, {
                deletedList: deletedSYS1020_R01List,
                selectedList: selectedSYS1020_R01List,
                dataList: SYS1020_R01List,
            }),
            (rowIndex, event) => {
                const insertCount = event.ctrlKey ? 10 : 1;
                for (let index = 0; index < insertCount; index += 1) {
                    handleInsertClick(event, rowIndex);
                }
            },
            (rowData) => RemoveRowHandler(
                gridRef,
                SYS1020_R01List,
                rowData,
                setClickable,
                removeSYS1020_R01List,
                addDeletedSYS1020_R01List,
            ),
        )),
        {
            headerName: "발생구분",
            colId: "messageSrc",
            field: "messageSrc",
            width: 180,
            ...CommonCodeColumn.options(
                messageSrcList,
                (params) => params.data?.inserted === true,
                CommonCodeColumn.change(updateSYS1020_R01List),
                undefined,
            ),
        },
        {
            headerName: "메시지구분",
            colId: "messageType",
            field: "messageType",
            width: 180,
            ...CommonCodeColumn.options(
                messageTypeList,
                (params) => params.data?.inserted === true,
                CommonCodeColumn.change(updateSYS1020_R01List),
                undefined,
            ),
        },
        {
            headerName: "메시지코드",
            colId: "messageCd",
            field: "messageCd",
            width: 180,
            editable: (params) => params.data?.inserted === true,
            pasteable: true,
            cellEditor: "agTextCellEditor",
            cellEditorParams: { maxLength: 20 },
        },
        {
            headerName: "메시지명",
            colId: "messageNm",
            field: "messageNm",
            minWidth: 350,
            flex: 1,
            editable: true,
            pasteable: true,
            cellEditor: "agTextCellEditor",
            cellEditorParams: { maxLength: 100 },
        },
    ], [
        SYS1020_R01List,
        addDeletedSYS1020_R01List,
        deletedSYS1020_R01List,
        gridRef,
        handleInsertClick,
        messageSrcList,
        messageTypeList,
        removeSYS1020_R01List,
        renderIndexCell,
        selectedSYS1020_R01List,
        setClickable,
        updateSYS1020_R01List,
    ]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
        [baseColumnDefs],
    );

    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<SYS1020_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        }) as ColDef<SYS1020_R01Dto>[];
    }, [baseColumnDefs, columnState]);

    const handleCellChange = useCellChangeHandler({
        dataList: SYS1020_R01List,
        originalDataList: originalSYS1020_R01List,
        updateRow: updateSYS1020_R01List,
    });

    const handleRowClick = useRowSelectionHandler({
        dataList: SYS1020_R01List,
        idKey: "valueIndex",
        selectedList: selectedSYS1020_R01List,
        deletedList: deletedSYS1020_R01List,
        addSelected: addSelectedSYS1020_R01List,
        removeSelected: removeSelectedSYS1020_R01List,
        clickable,
        setClickable,
    });

    const cellClickActions = useMemo<CustomCellClickAction<SYS1020_R01Dto>[]>(() => [
        {
            validate: (field, data) =>
                editableColumns.includes(field)
                && (!insertedOnlyColumns.includes(field) || data.inserted === true),
            action: (event) => event.api.startEditingCell({
                rowIndex: event.rowIndex!,
                colKey: event.colDef.field!,
            }),
        },
    ], []);

    const handleCellClick = CreateCellClickHandler(
        SYS1020_R01List,
        "valueIndex",
        cellClickActions,
    );

    const pinnedBottomRow = useMemo(() => [{
        messageSrc: `${SYS1020_R01List.length.toLocaleString()}건`,
    }], [SYS1020_R01List.length]);

    return (
        <GridContextWrapper<SYS1020_R01Dto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedSYS1020_R01List}
            defaultRowData={defaultSYS1020_R01Row}
            allSelected={allSelectedSYS1020_R01List}
            baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Excel<SYS1020_R01Dto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={gridDefaultColDef}
                rowClassRules={createRowClassRules<SYS1020_R01Dto>({
                    idKey: "valueIndex",
                    selectedList: selectedSYS1020_R01List,
                    deletedList: deletedSYS1020_R01List,
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
